import { userRoles } from '~/shared/constants'
import { parseISO } from 'date-fns'
import { ChangeEvent } from 'react'
import { BusinessUnit } from '~/api/businessUnits'
import { childrenToString } from '~/common/childrenUtils'
import { formatFiltersApiTime } from '~/common/dateUtils'
import { JSFSelect } from '~/components/jsonschema/VndlyJsonSchemaFormBase/widgets/select'
import { CheckboxList } from '~/components/v2/CheckboxList'
import AsyncCheckboxList from '~/components/v2/CheckboxList/AsyncCheckboxList'
import { CheckboxListCheckbox } from '~/components/v2/CheckboxList/CheckboxListCheckbox'
import { LegacyDateRangePicker, LegacyTimeRangePicker } from '~/components/v2/DatePicker'
import { Stack } from '~/design_system'
import {
  arrayUrlParser,
  booleanUrlParser,
  dateUrlParser,
  numberUrlParser,
  stringUrlParser,
  timeUrlParser
} from '~/hooks/useUrlState'
import { VndlyBusinessUnitPickerv2 } from '~/components/v2/VndlyBusinessUnitPicker'
import { useFilterInternalValues, usePatchFilterValues } from './FiltersProvider'
import {
  BooleanFilterConfig,
  BusinessUnitFilterConfig,
  DateRangeFilterConfig,
  FilterComponentProps,
  ListFilterConfig,
  SelectFilterConfig,
  TimeRangeFilterConfig,
  HiddenNumberFilterConfig
} from './types'

type ExcludePrivateConfigKeys<TConfig> = Omit<
  TConfig,
  'component' | 'urlParser' | 'debounceMs' | 'onClear' | 'onRestore' | 'listType' | '__type'
>

export const filter = {
  checkboxList: {
    asStrings<T extends ExcludePrivateConfigKeys<ListFilterConfig<string>>>(config: T) {
      return {
        __type: 'list' as const,
        component: CheckboxListFilter,
        listType: 'string' as const,
        urlParser: {
          [config.id]: arrayUrlParser(stringUrlParser)(config.id)
        },
        onClear: { [config.id]: [] },
        ...config
      }
    },
    asNumbers<T extends ExcludePrivateConfigKeys<ListFilterConfig<number>>>(config: T) {
      return {
        __type: 'list' as const,
        component: CheckboxListFilter,
        listType: 'number' as const,
        urlParser: {
          [config.id]: arrayUrlParser(numberUrlParser)(config.id)
        },
        onClear: { [config.id]: [] },
        ...config
      }
    }
  },

  buPicker<T extends ExcludePrivateConfigKeys<BusinessUnitFilterConfig>>({ v2, ...config }: T & { v2?: boolean }) {
    return {
      __type: 'businessUnit' as const,
      component: v2 ? BusinessUnitFilterV2 : BusinessUnitFilter,
      urlParser: { [config.id]: arrayUrlParser(numberUrlParser)(config.id) },
      onClear: { [config.id]: [] },
      ...config
    }
  },

  dateRange<T extends ExcludePrivateConfigKeys<DateRangeFilterConfig>>(config: T) {
    const toKey = `${config.id}_to` as const
    const fromKey = `${config.id}_from` as const
    return {
      __type: 'dateRange' as const,
      component: DateRangeFilter,
      urlParser: {
        [toKey]: dateUrlParser(toKey),
        [fromKey]: dateUrlParser(fromKey)
      },
      onClear: { [toKey]: undefined, [fromKey]: undefined },
      ...config
    }
  },

  timeRange<T extends ExcludePrivateConfigKeys<TimeRangeFilterConfig>>(config: T) {
    const toKey = `${config.id}_to` as const
    const fromKey = `${config.id}_from` as const
    return {
      __type: 'timeRange' as const,
      component: TimeRangeFilter,
      urlParser: {
        [toKey]: timeUrlParser(toKey),
        [fromKey]: timeUrlParser(fromKey)
      },
      onClear: { [toKey]: undefined, [fromKey]: undefined },
      ...config
    }
  },

  select: {
    asStrings<T extends ExcludePrivateConfigKeys<SelectFilterConfig<string>>>(config: T) {
      return {
        __type: 'select' as const,
        component: SelectFilter,
        listType: 'string' as const,
        urlParser: {
          [config.id]: arrayUrlParser(stringUrlParser)(config.id)
        },
        onClear: { [config.id]: [] },
        ...config
      }
    },
    asNumbers<T extends ExcludePrivateConfigKeys<SelectFilterConfig<number>>>(config: T) {
      return {
        __type: 'select' as const,
        component: SelectFilter,
        listType: 'number' as const,
        urlParser: {
          [config.id]: arrayUrlParser(numberUrlParser)(config.id)
        },
        onClear: { [config.id]: [] },
        ...config
      }
    }
  },

  singleCheckbox<T extends ExcludePrivateConfigKeys<BooleanFilterConfig>>(config: T) {
    return {
      __type: 'boolean' as const,
      component: SingleCheckboxFilter,
      urlParser: { [config.id]: booleanUrlParser(config.id) },
      onClear: { [config.id]: false },
      ...config
    }
  },

  hidden: {
    asNumber(config: ExcludePrivateConfigKeys<HiddenNumberFilterConfig>): HiddenNumberFilterConfig {
      return {
        __type: 'hidden',
        component: () => null,
        urlParser: { [config.id]: numberUrlParser(config.id) },
        onClear: { [config.id]: undefined },
        ...config
      }
    }
  }
}

/**
 * Filters a record of filter configs by user role, returning only the filters the user
 * has access to. Preserves the specific filter key types via `Partial<T>`.
 *
 * @example
 * const config = filterByRole(filters, user?.role)
 */
export function filterByRole<T extends Record<string, { allowedFor?: string[] } & Record<string, any>>>(
  filters: T,
  userRole: string | undefined
): Partial<T> {
  return Object.fromEntries(
    Object.entries(filters).filter(([_, f]) => {
      return f.allowedFor?.every(role => role === userRoles.All || role === userRole)
    })
  ) as Partial<T>
}

export function CheckboxListFilter({ filter }: FilterComponentProps<ListFilterConfig<string | number>>) {
  const value = useFilterInternalValues()[filter.id]
  const patchValues = usePatchFilterValues()
  const onChange = (value: string[] | number[]) => patchValues({ [filter.id]: value })

  return filter.optionsFromApi ? (
    <AsyncCheckboxList
      id={filter.id}
      config={filter.config}
      optionsFromApi={filter.optionsFromApi}
      onChange={onChange}
      value={value}
    />
  ) : (
    <CheckboxList
      // @ts-ignore
      config={filter.config}
      options={filter.options}
      onChange={onChange}
      value={value}
    />
  )
}

export function SingleCheckboxFilter({ filter }: FilterComponentProps<BooleanFilterConfig>) {
  const value = useFilterInternalValues()[filter.id]
  const patchValues = usePatchFilterValues()
  const onChange = (value: boolean) => patchValues({ [filter.id]: value })

  return (
    <CheckboxListCheckbox
      id={filter.id}
      truncate={false}
      w="100%"
      alignItems="center"
      isChecked={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.checked)
      }}>
      {filter.checkboxLabel}
    </CheckboxListCheckbox>
  )
}

export function BusinessUnitFilter({ filter }: FilterComponentProps<BusinessUnitFilterConfig>) {
  const value = useFilterInternalValues()[filter.id]
  const patchValues = usePatchFilterValues()
  const onChange = (value: number[]) => patchValues({ [filter.id]: value })

  return (
    <VndlyBusinessUnitPickerv2
      isMulti
      value={value}
      onChange={({ value }: { value: BusinessUnit[] }) => {
        onChange(value?.map(v => v.id))
      }}
      placeholderText={filter.placeholder}
    />
  )
}

export function BusinessUnitFilterV2({ filter }: FilterComponentProps<BusinessUnitFilterConfig>) {
  const value = useFilterInternalValues()[filter.id]
  const patchValues = usePatchFilterValues()
  const onChange = (value: number[]) => patchValues({ [filter.id]: value })

  return (
    <VndlyBusinessUnitPickerv2
      isMulti
      value={value}
      onChange={({ value }: { value: BusinessUnit[] }) => {
        onChange(value?.map(v => v.id))
      }}
      placeholderText={filter.placeholder}
    />
  )
}

export function SelectFilter({ filter }: FilterComponentProps<SelectFilterConfig<string | number>>) {
  const value = useFilterInternalValues()[filter.id]
  const patchValues = usePatchFilterValues()
  const onChange = (value: string[] | number[]) => patchValues({ [filter.id]: value })

  return (
    <JSFSelect
      // @ts-ignore
      value={value}
      onChange={onChange}
      multiple
      options={{
        optionsFromApi: filter.optionsFromApi,
        preloadFromApi: filter.preloadFromApi,
        valueLookupFromApi: filter.valueLookupFromApi
      }}
      closeMenuOnSelect={false}
      aria-label={filter.ariaLabel}
    />
  )
}

export function DateRangeFilter({ filter }: FilterComponentProps<DateRangeFilterConfig>) {
  const toKey = `${filter.id}_to` as const
  const fromKey = `${filter.id}_from` as const
  const values = useFilterInternalValues()
  const patchValues = usePatchFilterValues()

  return (
    <Stack mb={4} data-testid="date-range-filter">
      <LegacyDateRangePicker
        label={childrenToString(filter.title)}
        onChange={({ to, from }) => {
          patchValues({
            [toKey]: to ?? undefined,
            [fromKey]: from ?? undefined
          })
        }}
        value={{ to: values[toKey], from: values[fromKey] }}
        isClearable
        shouldWrapChildren={false}
      />
    </Stack>
  )
}

export function TimeRangeFilter({ filter }: FilterComponentProps<TimeRangeFilterConfig>) {
  const toKey = `${filter.id}_to` as const
  const fromKey = `${filter.id}_from` as const
  const values = useFilterInternalValues()
  const patchValues = usePatchFilterValues()

  function timeStringToDate(timeString: string) {
    if (!timeString) return null
    // I need parse to build a full date even though value is only the time, e.g 12:00
    const isoString = new Date().toISOString().slice(0, 10)
    const date = parseISO(`${isoString}T${timeString}`)
    if (Number.isNaN(date.getTime())) return null
    return date
  }

  return (
    <Stack mb={4} data-testid="date-range-filter">
      <LegacyTimeRangePicker
        label={childrenToString(filter.title)}
        onChange={({ to, from }) => {
          patchValues({
            [toKey]: to ? formatFiltersApiTime(to) : undefined,
            [fromKey]: from ? formatFiltersApiTime(from) : undefined
          })
        }}
        value={{
          to: timeStringToDate(values[toKey]),
          from: timeStringToDate(values[fromKey])
        }}
        isClearable
        shouldWrapChildren={false}
      />
    </Stack>
  )
}
